import { HttpClient } from 'effect/unstable/http'
import { dual } from 'effect/Function'
import { Class as PipeableClass } from 'effect/Pipeable'
import { Effect } from 'effect'
import type { CommandError } from './fetch-errors'
import {
  makeSDKHttpClient,
  type SDKHttpClient,
  type SDKRequestIdProvider,
  type SDKRequestHeadersProvider
} from './http-client'
import type { SDKCommand, SDKOperation } from './sdk.types'

export type SDKRequestFor<T extends SDKCommand['type']> = Extract<SDKCommand, { type: T }>
export type SDKOperationFor<T extends SDKCommand['type']> = Extract<
  SDKOperation,
  { request: { type: T } }
>

// Generated auth response types require `error`, although successful wire
// responses omit it. Normalize that foreign contract once at the SDK seam;
// callers must still require an error when the response says it failed.
type NormalizeSDKResponse<T> = T extends { mfaSession: infer Session; error: infer Error }
  ? Omit<T, 'error' | 'mfaSession'> & { error?: Error; mfaSession?: Session }
  : T extends { error: infer Error }
  ? Omit<T, 'error'> & { error?: Error }
  : T

export type SDKResponseFor<T extends SDKCommand['type']> = NormalizeSDKResponse<
  SDKOperationFor<T>['response']
>
export type STSDK = SDK
export type SDKHttpClientOperator = (httpClient: HttpClient.HttpClient) => HttpClient.HttpClient

type SDKOpts = {
  baseUrl: string
  httpClient: HttpClient.HttpClient
  getRequestHeaders?: SDKRequestHeadersProvider
  makeRequestId: SDKRequestIdProvider
}

export function createSTClientSDK({
  baseUrl,
  httpClient,
  getRequestHeaders,
  makeRequestId
}: SDKOpts): SDK {
  return new SDK(httpClient, { baseUrl, getRequestHeaders, makeRequestId })
}

class SDK extends PipeableClass {
  private readonly sdkHttpClient: SDKHttpClient

  constructor(
    private readonly httpClient: HttpClient.HttpClient,
    private readonly opts: {
      baseUrl: string
      getRequestHeaders?: SDKRequestHeadersProvider
      makeRequestId: SDKRequestIdProvider
    }
  ) {
    super()
    this.sdkHttpClient = makeSDKHttpClient(httpClient, opts)
  }

  send<K extends SDKCommand['type']>(
    request: SDKRequestFor<K>
  ): Effect.Effect<SDKResponseFor<K>, CommandError> {
    return Effect.map(this.sdkHttpClient.execute(request), (body) => body as SDKResponseFor<K>)
  }

  mapHttpClient(operator: SDKHttpClientOperator): SDK {
    return new SDK(operator(this.httpClient), this.opts)
  }
}

export const mapHttpClient: {
  (operator: SDKHttpClientOperator): (self: SDK) => SDK
  (self: SDK, operator: SDKHttpClientOperator): SDK
} = dual(2, (self: SDK, operator: SDKHttpClientOperator) => self.mapHttpClient(operator))
